import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Shield, AlertTriangle, Brain, Lock, Users, Scale } from "lucide-react";

const LegalDisclaimer = ({ user, apiClient }) => {
  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="legal-disclaimer-page">
      <Sidebar user={user} apiClient={apiClient} />
      
      <main className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Legal Disclaimer</h1>
                <p className="text-gray-300">IPOLabs Private Limited</p>
              </div>
            </div>
            <p className="text-gray-400">
              Please read this disclaimer carefully before using our platform services.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-8 py-12">
          {/* Section 1 */}
          <Card className="border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                1. General Nature of the Platform (Intermediary Status)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                IPOLabs Private Limited (the "Platform") is a technology-driven Intermediary Platform as defined under the <strong className="text-black">Information Technology Act, 2000</strong>. It serves as a digital bridge between MSMEs and independent professionals/agencies.
              </p>
              <p>
                IPOLabs Private Limited does not provide legal, financial, or tax advice directly. The inclusion of any professional or funding partner on the Platform does not constitute an endorsement, guarantee, or warranty by IPOLabs Private Limited.
              </p>
            </CardContent>
          </Card>

          {/* Section 2 */}
          <Card className="border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                2. No Investment or Financial Advice (SEBI Compliance)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">Informational Purposes Only</h4>
                <p className="text-orange-700 text-sm">
                  The "IPO Funding Module" and "Eligibility Quiz" are powered by Artificial Intelligence and are intended solely for educational and informational purposes.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">Not a SEBI Registered Adviser</h4>
                <p>
                  IPOLabs Private Limited is not a SEBI-registered Investment Adviser (IA) or Research Analyst (RA). The scores or "Eligibility Tier" generated by the AI are based on user-input data and do not constitute a "recommendation" to buy, sell, or hold securities.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">Independent Due Diligence</h4>
                <p>
                  Users are strongly advised to consult with a registered Merchant Banker or a qualified Financial Consultant before making any IPO-related decisions.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 3 */}
          <Card className="border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                3. Professional Matchmaking & Third-Party Services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <div>
                <h4 className="font-semibold text-black mb-2">Due Diligence</h4>
                <p>
                  IPOLabs Private Limited facilitates discovery but does not verify the absolute accuracy of every professional's "Past IPO Track Record." The responsibility for conducting due diligence, background checks, and fee negotiations lies entirely with the User (the SME).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">No Service Guarantee</h4>
                <p>
                  IPOLabs Private Limited is not liable for any deficiency in service, professional negligence, or failed IPO outcomes resulting from professionals hired through the Matchmaking Module.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 4 */}
          <Card className="border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-cyan-600" />
                </div>
                4. AI & Technology Limitations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <h4 className="font-semibold text-cyan-800 mb-2">AI Accuracy</h4>
                <p className="text-cyan-700 text-sm">
                  While our AI models are trained on contemporary regulatory data, they are susceptible to "hallucinations" or errors. Users should not rely solely on AI-generated summaries or transcriptions for legal or compliance filing.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">Transcription Disclaimer</h4>
                <p>
                  Automated call transcriptions and "Action Items" are provided for convenience only. IPOLabs Private Limited does not guarantee 100% accuracy in audio-to-text conversion.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 5 */}
          <Card className="border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                5. Privacy & Data Security (DPDP Act 2023)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <div>
                <h4 className="font-semibold text-black mb-2">Recording Consent</h4>
                <p>
                  By using the Video Consultation Module, users acknowledge and consent to the recording of sessions for their own documentation.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">Data Residency</h4>
                <p>
                  All sensitive data, including mobile numbers and financial inputs, are stored on servers located within the territory of India in compliance with the <strong className="text-black">DPDP Act 2023</strong>.
                </p>
              </div>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} IPOLabs Private Limited. All rights reserved.</p>
            <p className="mt-2">Last updated: February 2026</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LegalDisclaimer;
